import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import * as Yup from 'yup';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import BackIcon from '@assets/images/BackIcon.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FACILITY_USER_ROUTE_PATH,
  FACILITY_USER_VIEW_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomSelect from '@ui-components/Select/CustomSelect';
import CustomAutoComplete from '@ui-components/AutoComplete/CustomAutoComplete';
import CustomAutoCompleteSingleSelection from '@ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import CustomSwitch from '@ui-components/Switch/CustomSwitch';
import {
  useCheckEmailUnique,
  useCheckUsernameUniq,
  useGetFacilityUserDetailsById,
  useGetFacilityUserMetaData,
  useSaveOrUpdateFacility,
} from '../../API/hooks/FacilityUserHook';
import { Form, Formik, useFormikContext } from 'formik';
import type { FormikProps } from 'formik';
import type {
  ActiveFacilities,
  FacilityUserData,
} from '../../API/services/FacilityUserService';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';
import { useDebounce } from 'use-debounce';
import ConfirmationPopup from '../components/ConfirmationPopup';

const facilityUserInitialValues: FacilityUserData = {
  id: 0,
  firstName: '',
  lastName: '',
  primaryEmail: '',
  username: '',
  roleId: 0,
  facilityId: 0,
  managedFacilityIds: [] as ActiveFacilities[],
  status: true,
  password: '',
  notes: '',
  mfaEnabledByAdmin: true,
  isLocked: false,
};

const AddNewFacilityUser: React.FC = () => {
  const formikRef = useRef<FormikProps<FacilityUserData>>(null);
  const [primaryEmail, setPrimaryEmail] = React.useState<string | null>(null);
  const [userName, setUserName] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId') ?? '0';
  const { facilityUserId } = useParams<{ facilityUserId?: string }>();
  const [openFacilityUserConfirmDialog, setOpenFacilityUserConfirmDialog] =
    useState(false);

  const { facilityUserMetaData } = useGetFacilityUserMetaData(userId);

  const facilityUserddlId = facilityUserMetaData?.roles?.find(
    (item) => item.name.toLocaleLowerCase() === 'facility user'
  )?.id;

  const caseManagerddlId = facilityUserMetaData?.roles?.find(
    (item) => item.name.toLocaleLowerCase() === 'case manager'
  )?.id;

  const [initialValues, setInitialValues] = useState(facilityUserInitialValues);

  const [facilityUserSubmitAction, setFacilityUserSubmitAction] =
    useState<string>('');

  const { FacilityUserDetailsById, isLoading } = useGetFacilityUserDetailsById(
    userId,
    Number(facilityUserId)
  );

  const [debouncedEmail] = useDebounce(primaryEmail, 500);
  const editingUserId = facilityUserId ? Number(facilityUserId) : undefined;
  const { FacilityUserUniqEmail } = useCheckEmailUnique(
    debouncedEmail ?? '',
    editingUserId
  );

  const [debouncedUserName] = useDebounce(userName, 500);
  const { UserNameUniq } = useCheckUsernameUniq(
    debouncedUserName ?? '',
    editingUserId
  );

  type TakenValue = { value: string; message?: string } | null;
  const emailTakenRef = useRef<TakenValue>(null);
  const usernameTakenRef = useRef<TakenValue>(null);

  const applyUniqResult = (
    field: 'primaryEmail' | 'username',
    result: { isAvailable: boolean; message?: string } | undefined,
    value: string | null,
    ref: React.MutableRefObject<TakenValue>
  ) => {
    if (!result || !formikRef.current) return;
    if (result.isAvailable) {
      ref.current = null;
      return;
    }
    ref.current = { value: value ?? '', message: result.message };
    formikRef.current.setFieldTouched(field, true, false);
    formikRef.current.setFieldError(field, result.message);
  };

  useEffect(() => {
    applyUniqResult(
      'primaryEmail',
      FacilityUserUniqEmail,
      debouncedEmail,
      emailTakenRef
    );
  }, [FacilityUserUniqEmail]);

  useEffect(() => {
    applyUniqResult(
      'username',
      UserNameUniq,
      debouncedUserName,
      usernameTakenRef
    );
  }, [UserNameUniq]);
  const [confirmationPopup, setConfirmationPopup] = useState(false);

  const handleOpenFacilityUserConfirmDialog = (state: boolean) => {
    setOpenFacilityUserConfirmDialog(state);
  };

  useEffect(() => {
    if (FacilityUserDetailsById) {
      setInitialValues({
        id: FacilityUserDetailsById.id,
        firstName: FacilityUserDetailsById.firstName,
        lastName: FacilityUserDetailsById.lastName,
        primaryEmail: FacilityUserDetailsById.primaryEmail,
        username: FacilityUserDetailsById.username,
        roleId: FacilityUserDetailsById.roleId,
        facilityId: FacilityUserDetailsById.facilityId,
        managedFacilityIds: FacilityUserDetailsById.managedFacilityIds || [],
        status: FacilityUserDetailsById.status,
        password: FacilityUserDetailsById.password,
        notes: FacilityUserDetailsById.notes,
        mfaEnabledByAdmin: FacilityUserDetailsById.mfaEnabledByAdmin,
        isLocked: FacilityUserDetailsById.isLocked,
      });
    }
  }, [FacilityUserDetailsById]);

  const onSuccess = () => {
    if (facilityUserId) {
      navigate(`${FACILITY_USER_ROUTE_PATH}?toast=update`);
    } else {
      navigate(`${FACILITY_USER_ROUTE_PATH}?toast=success`);
    }
  };

  const { mutate: saveOrUpdateFacilityUser } =
    useSaveOrUpdateFacility(onSuccess);

  const handleFacilityUserSaveOrUpdate = async (
    facilityUserData: typeof facilityUserInitialValues
  ) => {
    const { ...payload } = facilityUserData;

    if (facilityUserSubmitAction) {
      saveOrUpdateFacilityUser({
        userId,
        action: facilityUserSubmitAction,
        ...payload,
      });
    }
  };

  const facilityUserValidationSchema = Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    primaryEmail: Yup.string()
      .required('Primary Email is required')
      .email('Invalid email')
      .test('email-unique', (val, context) => {
        const taken = emailTakenRef.current;
        if (!taken || val !== taken.value) return true;
        return context.createError({ message: taken.message });
      }),
    username: Yup.string()
      .required('Username is required')
      .max(50, 'Username must be no more than 50 characters long.')
      .test('username-unique', (val, context) => {
        const taken = usernameTakenRef.current;
        if (!taken || val !== taken.value) return true;
        return context.createError({ message: taken.message });
      }),
    roleId: Yup.number()
      .required('Role is required')
      .min(1, 'Role is required'),
    facilityId: Yup.number().when('roleId', {
      is: (value: number) => value === facilityUserddlId,
      then: (schema) => schema.min(1, 'Facility is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    managedFacilityIds: Yup.array().when('roleId', {
      is: (value: number) => value === caseManagerddlId,
      then: (schema) => schema.min(1, 'Managed Facility is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    password: Yup.string().max(
      50,
      'Password must be no more than 50 characters long.'
    ),
    notes: Yup.string().max(
      500,
      'Notes must be no more than 500 characters long.'
    ),
  });

  const handleFacilityUserBackButtonClick = () => {
    if (facilityUserId && !location.state?.isFacilityUserList) {
      navigate(FACILITY_USER_VIEW_ROUTE_PATH + `/${facilityUserId}`);
    } else {
      navigate(FACILITY_USER_ROUTE_PATH);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  // Component to handle role change side effects
  const RoleChangeHandler: React.FC<{
    facilityUserId: number | undefined;
    caseManagerddlId: number | undefined;
    facilityUserddlId: number | undefined;
  }> = ({ facilityUserId, caseManagerddlId, facilityUserddlId }) => {
    const { values, setFieldValue } = useFormikContext<FacilityUserData>();
    const prevRoleIdRef = useRef(values.roleId);

    useEffect(() => {
      if (facilityUserId) return;
      if (prevRoleIdRef.current === values.roleId) return;
      prevRoleIdRef.current = values.roleId;
      if (values.roleId === caseManagerddlId) {
        setFieldValue('facilityId', 0, false);
      } else if (values.roleId === facilityUserddlId) {
        setFieldValue('managedFacilityIds', [], false);
      }
    }, [
      values.roleId,
      facilityUserId,
      setFieldValue,
      caseManagerddlId,
      facilityUserddlId,
    ]);

    return null;
  };

  return (
    <Box>
      <Box>
        <ConfirmationPopup
          openPopup={confirmationPopup}
          onClosePopup={() => {
            setConfirmationPopup(false);
          }}
          title={'Confirmation Action'}
          message={
            ' Are you sure you want to disable 2FA for this user? This will allow login without two-factor authentication.'
          }
          onConfirm={() => {
            setConfirmationPopup(false);
            formikRef.current?.setFieldValue('mfaEnabledByAdmin', false);
          }}
        />
      </Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="text-align-left display-flex">
              <CustomIconButton
                id="back-button"
                icon={<BackIcon />}
                onClick={() => {
                  handleFacilityUserBackButtonClick();
                }}
              ></CustomIconButton>

              <CustomTypography
                className="pl-8"
                variant="h4"
                id="add-new-facility-user-text"
              >
                {facilityUserId
                  ? 'Edit Facility User'
                  : 'Add New Facility User'}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={facilityUserValidationSchema}
        onSubmit={(values) => {
          handleFacilityUserSaveOrUpdate(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
        }) => {
          return (
            <Form>
              <RoleChangeHandler
                facilityUserId={
                  facilityUserId ? Number(facilityUserId) : undefined
                }
                caseManagerddlId={caseManagerddlId}
                facilityUserddlId={facilityUserddlId}
              />
              <Box className="form-background p-24">
                <Grid2 container>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="user-info-text"
                    >
                      User Information
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Box className="display-flex">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="first-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        First Name <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomTextField
                        id="txt-first-name"
                        name="firstName"
                        placeholder="Type First Name"
                        size="small"
                        value={values.firstName}
                        onChange={(event) => {
                          setFieldValue('firstName', event.target.value);
                        }}
                      />
                      {errors?.firstName && touched?.firstName && (
                        <CustomTypography
                          id="validate-facility-user-firstname"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.firstName}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="last-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Last Name <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomTextField
                        id="txt-last-name"
                        name="lastName"
                        placeholder="Type Last Name"
                        size="small"
                        value={values.lastName}
                        onChange={(event) => {
                          setFieldValue('lastName', event.target.value);
                        }}
                      />
                      {errors?.lastName && touched?.lastName && (
                        <CustomTypography
                          id="validate-facility-user-lastname"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.lastName}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="user-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Username <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomTextField
                        id="txt-user-name"
                        name="userName"
                        placeholder="Type Username"
                        size="small"
                        value={values.username}
                        onChange={(event) => {
                          setFieldValue('username', event.target.value);
                          if (values.roleId === Number(facilityUserddlId)) {
                            setFieldValue('password', event.target.value);
                          }
                          setUserName(
                            event.target.value === ''
                              ? null
                              : event.target.value
                          );
                        }}
                        onBlur={() => setFieldTouched('username', true)}
                      />
                      {errors?.username && touched?.username && (
                        <CustomTypography
                          id="validate-facility-user-username"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.username}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="primary-email-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Primary Email <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomTextField
                        id="txt-primary-email"
                        name="primaryEmail"
                        placeholder="Type Primary Email"
                        size="small"
                        value={values.primaryEmail}
                        onChange={(event) => {
                          setFieldValue('primaryEmail', event.target.value);
                          setPrimaryEmail(
                            event.target.value === ''
                              ? null
                              : event.target.value
                          );
                        }}
                        onBlur={() => setFieldTouched('primaryEmail', true)}
                      />

                      {errors?.primaryEmail && touched?.primaryEmail && (
                        <CustomTypography
                          id="validate-facility-user-primaryemail"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.primaryEmail}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="role-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Role <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomSelect
                        id="facility-user-role"
                        placeholder="Select Role"
                        size="small"
                        options={facilityUserMetaData?.roles ?? []}
                        onChange={(value) => {
                          setFieldValue('roleId', Number(value));
                          setFieldValue('facilityId', 0);
                          setFieldValue('managedFacilityIds', []);
                          if (Number(value) === Number(facilityUserddlId)) {
                            setFieldValue('password', values.username);
                          }
                        }}
                        selectedValue={values.roleId}
                        fullWidth
                      />
                      {errors?.roleId && touched?.roleId && (
                        <CustomTypography
                          id="validate-facility-user-role"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.roleId}
                        </CustomTypography>
                      )}
                    </Grid2>

                    {values.roleId === Number(facilityUserddlId) && (
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="facility-staff-facility-name-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Facility Name <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomAutoCompleteSingleSelection
                          id="facility-staff-facility-name"
                          placeholder="Select Facility Name"
                          size="small"
                          options={facilityUserMetaData?.activeFacilities ?? []}
                          value={
                            facilityUserMetaData?.activeFacilities?.find(
                              (x) => x.id === values.facilityId
                            ) ?? null
                          }
                          onChange={(selectedOptions) => {
                            const selectedId = selectedOptions
                              ? selectedOptions.id
                              : 0;
                            setFieldValue('facilityId', selectedId, false);
                          }}
                        />
                        {errors?.facilityId && touched?.facilityId && (
                          <CustomTypography
                            id="validate-facility-user-facilityid"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.facilityId}
                          </CustomTypography>
                        )}
                      </Grid2>
                    )}
                    {values.roleId === Number(caseManagerddlId) && (
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="case-manager-managed-facilities-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Managed Facilities{' '}
                          <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomAutoComplete
                          id="case-manager-managed-facilities"
                          placeholder="Select Managed Facilities"
                          size="small"
                          options={facilityUserMetaData?.activeFacilities ?? []}
                          value={values.managedFacilityIds}
                          onChange={(selectedOptions) => {
                            setFieldValue(
                              'managedFacilityIds',
                              selectedOptions,
                              false
                            );
                          }}
                          disableCloseOnSelect={true}
                        />
                        {errors?.managedFacilityIds &&
                          touched?.managedFacilityIds && (
                            <CustomTypography
                              id="validate-facility-user-managedFacilityIds"
                              color="error"
                              variant="body3"
                              className="pt-8 text-align-left"
                            >
                              {typeof errors.managedFacilityIds === 'string' &&
                                errors.managedFacilityIds}
                            </CustomTypography>
                          )}
                      </Grid2>
                    )}
                    {values.roleId === Number(facilityUserddlId) && (
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="primary-email-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Password
                        </CustomTypography>
                        <CustomTextField
                          id="txt-primary-email"
                          name="password"
                          placeholder="Password"
                          size="small"
                          value={values.password}
                          isPassword={true}
                          disabled
                        />

                        {errors?.password && touched?.password && (
                          <CustomTypography
                            id="validate-facility-user-primaryemail"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.password}
                          </CustomTypography>
                        )}
                      </Grid2>
                    )}
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-user-status-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Status
                      </CustomTypography>
                      <Box className="text-align-left">
                        <CustomSwitch
                          id="facility-user-status-switch"
                          checked={values.status}
                          onChange={(event) => {
                            setFieldValue('status', event.target.checked);
                          }}
                          labelPlacement="end"
                          label={values.status ? 'Active' : 'Inactive'}
                        />
                      </Box>
                    </Grid2>
                    {values.roleId === Number(caseManagerddlId) && (
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="facility-user-status-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Two Factor Authentication
                        </CustomTypography>
                        <Box className="text-align-left">
                          <CustomSwitch
                            id="facility-user-status-switch"
                            checked={values.mfaEnabledByAdmin}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setFieldValue(
                                  'mfaEnabledByAdmin',
                                  event.target.checked
                                );
                              } else {
                                setConfirmationPopup(true);
                              }
                            }}
                            labelPlacement="end"
                            label={values.mfaEnabledByAdmin ? 'On' : 'Off'}
                          />
                        </Box>
                      </Grid2>
                    )}
                  </Grid2>
                </Box>
                <Box className="pt-32">
                  <CustomDivider />
                </Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="facility-user-notes-text"
                      >
                        Facility User Notes
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                      <CustomTypography
                        id="notes-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Notes
                      </CustomTypography>
                      <CustomTextField
                        id="txt-notes"
                        placeholder="Type Notes"
                        size="small"
                        multiLine
                        rows={4}
                        value={values.notes}
                        onChange={(event) => {
                          setFieldValue('notes', event.target.value);
                        }}
                      ></CustomTextField>
                      {errors.notes && (
                        <CustomTypography
                          id="validate-fax-description"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.notes}
                        </CustomTypography>
                      )}
                    </Grid2>
                  </Grid2>
                </Box>
                <Box className="pt-32">
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <Box className="display-flex justify-end">
                        <Box className="pr-12">
                          <CustomButton
                            id="cancel-button"
                            onClick={() => {
                              handleOpenFacilityUserConfirmDialog(true);
                            }}
                            variant="outlined"
                          >
                            Cancel
                          </CustomButton>
                        </Box>
                        <Box>
                          <CustomButton
                            id="save-button"
                            onClick={() => {
                              setFacilityUserSubmitAction(
                                facilityUserId ? 'Update' : 'Save'
                              );
                              handleSubmit();
                              console.log('values', values);
                            }}
                            variant="contained"
                          >
                            {facilityUserId ? 'Update' : 'Save'}
                          </CustomButton>
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>

      <Box>
        <ConfirmDiscardDialog
          open={openFacilityUserConfirmDialog}
          onClose={() => handleOpenFacilityUserConfirmDialog(false)}
          navigateTo={
            facilityUserId && !location.state?.isFacilityUserList
              ? `${FACILITY_USER_VIEW_ROUTE_PATH}/${facilityUserId}`
              : FACILITY_USER_ROUTE_PATH
          }
        />
      </Box>
    </Box>
  );
};

export default AddNewFacilityUser;
